import React from "react";
import { useTranslation } from "react-i18next";
import "./TelealarmeISSCallFlow.css";

/**
 * Section 04 — Parcours d'appel ISS.
 * Version V2 : visuels plus premium et plus explicites.
 * Aucun changement du message métier validé.
 */
const CONTENT = {
  fr: {
    eyebrow: "LE FONCTIONNEMENT EN TROIS ÉTAPES",
    title: "En cas de besoin, un parcours d'appel simple à comprendre.",
    intro: "L'essentiel de la chaîne d'appel — sans protocole ni jargon inutile.",
    steps: [
      {
        id: "01",
        kind: "alarm",
        imageAlt: "Passagère utilisant le bouton d'alarme dans une cabine d'ascenseur",
        title: "Le passager déclenche l'alarme.",
        text: "Une action depuis l'interface d'appel en cabine."
      },
      {
        id: "02",
        kind: "connection",
        imageAlt: "Équipement de téléalarme destiné à établir la communication",
        title: "La téléalarme établit la liaison.",
        text: "La communication emprunte l'architecture prévue pour l'installation, selon les conditions du réseau et de l'alimentation."
      },
      {
        id: "03",
        kind: "assistance",
        imageAlt: "Opérateur d'un centre d'assistance équipé d'un casque et d'un écran",
        title: "Le centre d'assistance échange.",
        text: "Le service prend en charge l'appel selon sa procédure et les conditions de fonctionnement de l'installation."
      }
    ],
    note: "Parcours illustratif : la couverture réseau, l'alimentation, les dispositifs de secours et les essais sont à vérifier lors de l'étude technique."
  },
  en: {
    eyebrow: "HOW THE CALL WORKS IN THREE STEPS",
    title: "When help is needed, an easy-to-understand call process.",
    intro: "The essentials of the emergency call chain — without unnecessary technical jargon.",
    steps: [
      {
        id: "01",
        kind: "alarm",
        imageAlt: "Passenger pressing the emergency alarm button inside an elevator car",
        title: "The passenger triggers the alarm.",
        text: "A simple action using the in-car emergency call interface."
      },
      {
        id: "02",
        kind: "connection",
        imageAlt: "Emergency communication device used to establish the connection",
        title: "The emergency device establishes the connection.",
        text: "Communication uses the architecture designed for the installation, subject to network and power conditions."
      },
      {
        id: "03",
        kind: "assistance",
        imageAlt: "Assistance centre operator using a headset and a computer",
        title: "The assistance centre responds.",
        text: "The service handles the call according to its own procedures and the installation's operating conditions."
      }
    ],
    note: "Illustrative call process: network coverage, power supply, backup arrangements and call testing must be checked during the technical assessment."
  }
};

// Images are served from the React public/ directory (not imported from src/).
// Put appel_cabine.png, telealarme.png and call_center.png directly in public/.
// PUBLIC_URL also supports hosting the application under a URL subpath.
const PUBLIC_IMAGES_BASE = (process.env.PUBLIC_URL || "").replace(/\/$/, "");

function CallPhoto({ kind, alt, priority }) {
  const visuals = {
    alarm: { src: `${PUBLIC_IMAGES_BASE}/images/appel_cabine.png`, width: 1448, height: 1086 },
    connection: { src: `${PUBLIC_IMAGES_BASE}/images/telealarme.png`, width: 1536, height: 1086 },
    assistance: { src: `${PUBLIC_IMAGES_BASE}/images/call_center.png`, width: 1448, height: 1086 }
  };
  const visual = visuals[kind] || visuals.alarm;

  return (
    <img
      className={`iss-call__photo iss-call__photo--${kind}`}
      src={visual.src}
      alt={alt}
      width={visual.width}
      height={visual.height}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
    />
  );
}

function FlowArrow() {
  return (
    <span className="iss-call__connector" aria-hidden="true">
      <svg viewBox="0 0 52 32" fill="none" focusable="false">
        <path d="M5 16h35M29 5l11 11-11 11" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

export default function TelealarmeISSCallFlow() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const copy = CONTENT[language] || CONTENT.fr;

  return (
    <section id="iss-call-flow" className="iss-call" aria-labelledby="iss-call-title">
      <div className="iss-call__container">
        <header className="iss-call__header">
          <p className="iss-call__eyebrow">{copy.eyebrow}</p>
          <h2 className="iss-call__title" id="iss-call-title">{copy.title}</h2>
          <p className="iss-call__lead">{copy.intro}</p>
        </header>

        <div className="iss-call__timeline" aria-label={language === "en" ? "Emergency call process" : "Déroulement d'un appel d'urgence"}>
          {copy.steps.map((step, index) => (
            <React.Fragment key={step.id}>
              {index > 0 && <FlowArrow />}
              <article className="iss-call__card">
                <div className="iss-call__art">
                  <CallPhoto kind={step.kind} alt={step.imageAlt} priority={index === 0} />
                </div>
                <div className="iss-call__content">
                  <div className="iss-call__step">{language === "en" ? "STEP" : "ÉTAPE"} {step.id}</div>
                  <h3 className="iss-call__cardTitle">{step.title}</h3>
                  <p className="iss-call__cardText">{step.text}</p>
                </div>
              </article>
            </React.Fragment>
          ))}
        </div>

        <p className="iss-call__note">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true" focusable="false">
            <circle cx="12" cy="12" r="9.25" stroke="currentColor" strokeWidth="1.4" />
            <path d="M12 11v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <circle cx="12" cy="7.5" r="1" fill="currentColor" />
          </svg>
          <span>{copy.note}</span>
        </p>
      </div>
    </section>
  );
}
